import { useCallback, useMemo, useState } from "react";

import { useQueryClient } from "@tanstack/react-query";

import type { ISwSettings } from "app/@types/ISwSettings";
import { useAddonsCart } from "app/components/hooks/useAddonsCart";
import { NO_TOUCH_PURCHASE_QUERY_TAG } from "app/components/no-touch-purchase-modal/confirmation-modal/useNoTouchPurchaseQuery";
import {
    NO_TOUCH_PURCHASE_MODAL_STATE,
    type NoTouchPurchaseModalState,
} from "app/components/no-touch-purchase-modal/noTouchPurchaseModalConstants";
import { type INoTouchSuggestion, getNTPurchaseSuggestions } from "app/components/no-touch-purchase-modal/suggestions";
import type { INoTouchAddon } from "app/components/no-touch-purchase-modal/summary-modal/useNoTouchPurchaseSummaryModal";
import { useAddonsState } from "app/contexts/AddonStateContext/AddonStateContext";
import { ADDON_TYPE_NAME_DICT, type AddonTypeName } from "app/contexts/AddonStateContext/const";
import { getQueryKey } from "app/modules/api-client";
import { allTrackers } from "app/services/track/track";

interface IUseNoTouchPurchaseModalProps {
    swSettings: ISwSettings;
    addonType: AddonTypeName | null;
    setAddonType: (newAddonType: AddonTypeName | null) => void;
    onPurchaseAddonClick: (addonType: AddonTypeName | null, quantity?: number) => void;
}

interface IUseNoTouchPurchaseModalApi {
    isPrimaryModalOpen: boolean;
    modalState: NoTouchPurchaseModalState;
    selectedAddon?: INoTouchAddon;
    suggestions: INoTouchSuggestion[];
    handleCloseModal: () => void;
    handlePurchaseClick: () => void;
    handleConfirmClick: () => void;
    handleSelectedAddonChange: (selectedAddon?: INoTouchAddon) => void;
    handlePurchaseError: () => void;
    handlePurchasePaymentError: () => void;
    handlePurchaseSuccess: () => void;
}

export const useNoTouchPurchaseModal = ({
    swSettings,
    addonType,
    setAddonType,
    onPurchaseAddonClick,
}: IUseNoTouchPurchaseModalProps): IUseNoTouchPurchaseModalApi => {
    const [modalState, setModalState] = useState<NoTouchPurchaseModalState>(NO_TOUCH_PURCHASE_MODAL_STATE.Form);
    const [selectedAddon, setSelectedAddon] = useState<INoTouchAddon | undefined>(undefined);
    const { removeAddonFromCart } = useAddonsCart();
    const { getIsAddonAvailable } = useAddonsState();
    const queryClient = useQueryClient();

    // the purchase result is cached per addon, so it has to be dropped to let the user retry the same addon
    const resetPurchaseQuery = useCallback(() => {
        queryClient.removeQueries({ queryKey: getQueryKey({ tag: NO_TOUCH_PURCHASE_QUERY_TAG }) });
    }, [queryClient]);

    const handlePurchaseError = useCallback(() => {
        allTrackers.dispatchTrackEvent("popup", "show", `one click purchase/purchase error/${addonType}`);
        setModalState(NO_TOUCH_PURCHASE_MODAL_STATE.Error);
        setSelectedAddon(undefined);
    }, [addonType]);

    const handlePurchasePaymentError = useCallback(() => {
        allTrackers.dispatchTrackEvent("popup", "show", `one click purchase/purchase payment failure/${addonType}`);
        setModalState(NO_TOUCH_PURCHASE_MODAL_STATE.PaymentFailure);
        setSelectedAddon(undefined);
    }, [addonType]);

    const handlePurchaseSuccess = useCallback(() => {
        if (selectedAddon?.title) {
            removeAddonFromCart(selectedAddon.title);
        }
        allTrackers.dispatchTrackEvent("popup", "show", `one click purchase/purchase successful/${addonType}`);
        setModalState(NO_TOUCH_PURCHASE_MODAL_STATE.Success);
    }, [addonType, removeAddonFromCart, selectedAddon?.title]);

    const handlePurchaseClick = useCallback(() => {
        allTrackers.dispatchTrackEvent("popup", "click", `one click purchase/click on purchase/${addonType}`);
        resetPurchaseQuery();
        setModalState(NO_TOUCH_PURCHASE_MODAL_STATE.InProgress);
    }, [addonType, resetPurchaseQuery]);

    const handleConfirmClick = useCallback(() => {
        allTrackers.dispatchTrackEvent("popup", "show", `one click purchase/purchase successful done/${addonType}`);
        if (addonType === ADDON_TYPE_NAME_DICT.AdditionalUsersDynamic) {
            window.location.replace(`${swSettings.swsites.login}/user-management`);
        } else {
            window.location.reload();
        }
    }, [addonType, swSettings.swsites.login]);

    const handleCloseModal = useCallback(() => {
        onPurchaseAddonClick(null);
        setSelectedAddon(undefined);
        setAddonType(null);
        setModalState(NO_TOUCH_PURCHASE_MODAL_STATE.Form);
        resetPurchaseQuery();
    }, [onPurchaseAddonClick, setAddonType, resetPurchaseQuery]);

    const handleSelectedAddonChange = useCallback(
        (newAddon?: INoTouchAddon) => {
            if (newAddon) {
                allTrackers.dispatchTrackEvent(
                    "popup",
                    "click",
                    `one click purchase/select primary add on: ${newAddon.title}/${addonType}`,
                );
            }
            setSelectedAddon(newAddon);
        },
        [addonType],
    );

    const suggestions = useMemo(() => {
        if (!addonType) {
            return [];
        }

        return getNTPurchaseSuggestions(addonType, getIsAddonAvailable);
    }, [addonType, getIsAddonAvailable]);

    const isPrimaryModalOpen = useMemo(
        () =>
            addonType !== null &&
            modalState !== NO_TOUCH_PURCHASE_MODAL_STATE.Success &&
            modalState !== NO_TOUCH_PURCHASE_MODAL_STATE.InProgress &&
            modalState !== NO_TOUCH_PURCHASE_MODAL_STATE.PaymentFailure,
        [addonType, modalState],
    );

    return {
        isPrimaryModalOpen,
        modalState,
        selectedAddon,
        suggestions,
        handleCloseModal,
        handlePurchaseClick,
        handleConfirmClick,
        handleSelectedAddonChange,
        handlePurchaseError,
        handlePurchasePaymentError,
        handlePurchaseSuccess,
    };
};
